import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, firstValueFrom } from 'rxjs';
import { PurchasingGroupDataService } from '../../services/purchasing-group-data.service';

export class PurchasingGroupCompanyCodeDialogData {
    constructor(
        public readonly purchasingGroupId: string,
        public readonly assignedCompanyCodeIds: ReadonlyArray<string>,
    ) {}
}

export class PurchasingGroupCompanyCodeDialogResult {
    constructor(public readonly updated: boolean) {}
}

@Component({
    selector: 'lib-purchasing-group-company-code-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, BaseSiteLayoutModule, TranslateModule],
    templateUrl: './purchasing-group-company-code-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupCompanyCodeDialogComponent implements OnInit {
    private readonly companyCodeDataService = inject(CompanyCodeDataService);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject<
        MatDialogRef<PurchasingGroupCompanyCodeDialogComponent, PurchasingGroupCompanyCodeDialogResult>
    >(MatDialogRef);
    private readonly data = inject<PurchasingGroupCompanyCodeDialogData>(MAT_DIALOG_DATA);

    public readonly heading = marker('Zugewiesene Buchungskreise');
    public readonly icon = 'fas fa-building';
    public readonly companyCodesLoading = signal(false);
    public readonly saving = signal(false);
    /**
     * Inline feedback rendered in the dialog body. The toast notification is forwarded to the parent
     * shell via postMessage inside the legacy iframe and may never surface there, leaving an
     * add/remove silently confirmed or reverted — this banner always renders in the dialog's own DOM.
     */
    public readonly feedback = signal<{ level: 'success' | 'error'; message: string } | null>(null);
    public readonly pendingCompanyCodeId = new FormControl<string>('', { nonNullable: true });

    private readonly allCompanyCodes = signal<ReadonlyArray<ICompanyCode>>([]);
    private readonly assignedIds = signal<ReadonlyArray<string>>([...this.data.assignedCompanyCodeIds]);
    private updated = false;

    public readonly assignedCompanyCodes = computed<ReadonlyArray<ICompanyCode>>(() => {
        const byId = new Map(this.allCompanyCodes().map((cc) => [cc.id, cc]));
        return this.assignedIds()
            .map((id) => byId.get(id) ?? ({ id, code: id, name: id } satisfies ICompanyCode))
            .sort((left, right) => left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }));
    });

    public readonly availableCompanyCodes = computed<ReadonlyArray<ICompanyCode>>(() => {
        const assigned = new Set(this.assignedIds());
        return this.allCompanyCodes()
            .filter((cc) => !assigned.has(cc.id))
            .sort((left, right) => left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }));
    });

    public readonly assignedCount = computed(() => this.assignedIds().length);
    public readonly allAssigned = computed(() => this.availableCompanyCodes().length === 0);

    constructor() {
        this.pendingCompanyCodeId.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((id) => {
                if (id) {
                    void this.addCompanyCode(id);
                }
            });
    }

    public ngOnInit(): void {
        this.loadCompanyCodes();
    }

    public async remove(id: string): Promise<void> {
        if (this.saving()) return;
        const previous = this.assignedIds();
        const next = previous.filter((entry) => entry !== id);
        await this.persist(
            previous,
            next,
            marker('Buchungskreis konnte nicht entfernt werden.'),
            marker('Buchungskreis entfernt.'),
        );
    }

    public close(): void {
        this.dialogRef.close(new PurchasingGroupCompanyCodeDialogResult(this.updated));
    }

    private async addCompanyCode(id: string): Promise<void> {
        if (this.saving() || this.assignedIds().includes(id)) {
            this.pendingCompanyCodeId.setValue('', { emitEvent: false });
            return;
        }
        const previous = this.assignedIds();
        const next = [...previous, id];
        this.pendingCompanyCodeId.setValue('', { emitEvent: false });
        await this.persist(
            previous,
            next,
            marker('Buchungskreis konnte nicht hinzugefügt werden.'),
            marker('Buchungskreis hinzugefügt.'),
        );
    }

    private async persist(
        previous: ReadonlyArray<string>,
        next: ReadonlyArray<string>,
        fallbackErrorMessage: string,
        successMessage: string,
    ): Promise<void> {
        this.assignedIds.set(next);
        this.feedback.set(null);
        this.saving.set(true);
        try {
            await firstValueFrom(this.dataService.updateCompanyCodes(this.data.purchasingGroupId, next));
            this.updated = true;
            this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
            this.feedback.set({ level: 'success', message: this.translateService.instant(successMessage) });
        } catch (error) {
            this.assignedIds.set(previous);
            const message = this.dataService.resolveErrorMessage(error, fallbackErrorMessage);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            this.feedback.set({ level: 'error', message });
        } finally {
            this.saving.set(false);
        }
    }

    private loadCompanyCodes(): void {
        this.companyCodesLoading.set(true);
        this.companyCodeDataService
            .getAllActive()
            .pipe(finalize(() => this.companyCodesLoading.set(false)))
            .subscribe({
                next: (companyCodes) => this.allCompanyCodes.set([...companyCodes]),
                error: (error) => {
                    const message = this.companyCodeDataService.resolveErrorMessage(
                        error,
                        marker('Buchungskreise konnten nicht geladen werden.'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }
}
